import { apiv1 as api } from '@/adapters';
import type {
  ConnectorFeatureInvocationSequence as ApiConnectorFeatureInvocationSequence,
  ConnectorInvocationStatusModel as ApiConnectorInvocationStatusResponse,
  PageConnectorInvocationStatusModel as ApiPageConnectorInvocationStatusResponse,
  RequestConnectorInvocationResult,
  ConnectorFeatureModel,
  ConnectorModel,
  ReconcileConnectorFeature,
} from '@/adapters/apiv1.g';
import {
  extractAccepted,
  extractBoolean,
  extractConnector,
  extractConnectorFeature,
  extractInvocationResult,
  extractInvocationSequence,
  extractInvocationStatus,
  extractInvocationStatuses,
  extractPageModels,
  unwrapData,
} from '@/adapters/shared';
import { providerService } from '@/services/provider-service';
import type {
  Connector,
  ConnectorFeature,
  ConnectorFeatureCurrentSequence,
  InvokeConnectorFeatureResult,
} from '@/types/core/connector';
import type {
  ConnectorInvocationStatus,
  ConnectorInvocationStatuses,
} from '@/types/core/connector-invocation-status';
import type { Feature } from '@/types/core/provider';
import type { Page, PageRequest } from '@/types/shared/page';
import { toApiQuery } from '@/types/shared/page';

// OpenAPI moves feature_id/run_sequence into the path for invocation APIs, but
// fetch-gen still types all body properties as required, so we narrow at the call site.
type RequestConnectorInvocationBody = {
  run_at?: string;
  payload?: unknown;
};

type StopConnectorInvocationBody = {
  message?: string;
  outcome?: 'canceled' | 'failed' | 'completed';
  processed_count?: number;
};
import { retryCommand } from '@/utils/retry';
import { type UUID, newUUID } from '@/utils/uuid';

export type ConnectorFeatureWithProviderOptions = {
  connectorFeature: ConnectorFeature;
  providerFeature: Feature | null;
};

export const connectorService = {
  listConnectors: async (
    tenantId: UUID,
    query?: PageRequest
  ): Promise<Page<Connector>> => {
    const apiQuery = toApiQuery(query);
    const res = await api.listConnectors(tenantId, apiQuery);
    const rawModels = extractPageModels<ConnectorModel>(res);
    const models = rawModels.map(extractConnector);
    const next = res?.data?.next ?? null;
    const prev = res?.data?.prev ?? null;
    return { items: models, next, prev, limit: query?.limit };
  },

  /** Find a specific connector by id */
  findConnector: async (
    tenantId: UUID,
    connectorId: UUID
  ): Promise<Connector | null> => {
    const res = await api.findConnector(tenantId, connectorId);
    const model = unwrapData(res);
    if (!model) return null;
    return extractConnector(model);
  },

  createConnector: async (
    tenantId: UUID,
    connector: {
      name: string;
      providerId: UUID;
      requirements?: Array<string>;
    }
  ): Promise<Connector> => {
    // The id is generated once and reused on every retry, so re-issuing the
    // reconcile command is idempotent (no duplicate connectors).
    const connectorId = newUUID();
    await retryCommand(async () => {
      const res = await api.reconcileConnector(tenantId, connectorId, {
        name: connector.name ?? '',
        is_system: false,
        provider_id: connector.providerId,
        requirements: connector.requirements ?? [],
      });
      if (!extractAccepted(res)) {
        throw new Error('Failed to create connector');
      }
    });
    return { ...connector, id: connectorId } as Connector;
  },

  /** Update an existing connector (uses reconcile API) */
  updateConnector: async (
    tenantId: UUID,
    connectorId: UUID,
    updates: {
      name?: string;
      requirements?: Array<string>;
      requiredSites?: Array<string>;
    }
  ): Promise<Connector> => {
    const existing = await connectorService.findConnector(
      tenantId,
      connectorId
    );
    if (!existing) {
      throw new Error('Connector not found');
    }
    const res = await api.reconcileConnector(tenantId, connectorId, {
      name: updates.name ?? existing.name ?? '',
      is_system: existing.is_system ?? false,
      provider_id: existing.providerId,
      requirements: updates.requirements ?? [...(existing.requirements ?? [])],
      required_sites: updates.requiredSites ?? [
        ...(existing.requiredSites ?? []),
      ],
    });
    if (!extractAccepted(res)) {
      throw new Error('Failed to update connector');
    }
    return { ...existing, ...updates } as Connector;
  },

  /** List all features for a connector */
  listConnectorFeatures: async (
    tenantId: UUID,
    connectorId: UUID,
    query?: PageRequest
  ): Promise<Page<ConnectorFeature>> => {
    const apiQuery = toApiQuery(query);
    const res = await api.listConnectorFeatures(
      tenantId,
      connectorId,
      apiQuery
    );
    const models = extractPageModels<ConnectorFeatureModel>(res);
    const next = res?.data?.next ?? null;
    const prev = res?.data?.prev ?? null;
    return {
      items: models.map(extractConnectorFeature),
      next,
      prev,
      limit: query?.limit,
    };
  },

  /**
   * Combines connector feature state with provider feature options/schema data.
   * This keeps feature/provider joining logic in the service layer to avoid
   * race-prone composition in components.
   */
  listConnectorFeaturesWithProviderOptions: async (
    tenantId: UUID,
    connectorId: UUID,
    query?: PageRequest
  ): Promise<Array<ConnectorFeatureWithProviderOptions>> => {
    const connector = await connectorService.findConnector(
      tenantId,
      connectorId
    );
    if (!connector?.providerId) {
      throw new Error('Connector provider not ready, retrying');
    }

    const [connectorFeaturesPage, providerFeaturesPage] = await Promise.all([
      connectorService.listConnectorFeatures(tenantId, connectorId, query),
      providerService.listProviderFeatures(
        tenantId,
        connector.providerId,
        query
      ),
    ]);

    const isFirstPage = !query?.next;
    const hasFilter = query?.filter !== undefined;
    const isLikelyEventualConsistencyGap =
      isFirstPage &&
      !hasFilter &&
      connectorFeaturesPage.items.length === 0 &&
      providerFeaturesPage.items.length > 0;

    if (isLikelyEventualConsistencyGap) {
      throw new Error('Empty connector feature response, retrying');
    }

    const providerFeatureById = new Map(
      providerFeaturesPage.items.map((feature) => [feature.id, feature])
    );

    return connectorFeaturesPage.items.map((connectorFeature) => ({
      connectorFeature,
      providerFeature:
        providerFeatureById.get(connectorFeature.featureId) ?? null,
    }));
  },

  /**
   * Upsert connector feature (reconcileConnectorFeature). Omit patch fields you do not want to change.
   * secretIds is keyed by credential slot name and is additive: only the listed slots are
   * added/updated, omitted slots are left unchanged. Use unbindConnectorFeatureCredential to remove
   * a binding.
   */
  reconcileConnectorFeature: async (
    tenantId: UUID,
    connectorId: UUID,
    featureId: UUID,
    patch: {
      enabled?: boolean;
      options?: Record<string, unknown>;
      secretIds?: Record<string, UUID>;
      requirements?: Array<string>;
    }
  ): Promise<boolean> => {
    const body: ReconcileConnectorFeature = {
      tenant_id: tenantId,
      connector_id: connectorId,
      feature_id: featureId,
    };
    if (patch.enabled !== undefined) {
      body.enabled = patch.enabled;
    }
    if (patch.options !== undefined) {
      body.options = patch.options;
    }
    if (patch.secretIds !== undefined) {
      body.secret_ids = patch.secretIds;
    }
    if (patch.requirements !== undefined) {
      body.requirements = patch.requirements;
    }
    // reconcileConnectorFeature is an upsert keyed by featureId, so retrying a
    // transient non-accepted response (e.g. the connector aggregate not yet
    // projected) is idempotent and saves the user a manual refresh + resubmit.
    return retryCommand(async () => {
      const res = await api.reconcileConnectorFeature(
        tenantId,
        connectorId,
        featureId,
        body
      );
      if (!extractAccepted(res)) {
        throw new Error('Failed to reconcile connector feature');
      }
      return true;
    });
  },

  /**
   * Unbind a single credential slot from a connector feature. This is the explicit way to remove a
   * binding (reconcile is additive and never implicitly unbinds). Idempotent if the slot is unbound.
   */
  unbindConnectorFeatureCredential: async (
    tenantId: UUID,
    connectorId: UUID,
    featureId: UUID,
    name: string
  ): Promise<boolean> => {
    const res = await api.unbindConnectorFeatureCredential(
      tenantId,
      connectorId,
      featureId,
      { name }
    );
    return extractBoolean(res);
  },

  /** Find a specific connector feature by connector id and provider feature id */
  findConnectorFeature: async (
    tenantId: UUID,
    connectorId: UUID,
    featureId: UUID
  ): Promise<ConnectorFeature | null> => {
    try {
      const res = await api.findConnectorFeature(
        tenantId,
        connectorId,
        featureId
      );
      const model = unwrapData(res);
      if (!model) return null;
      return extractConnectorFeature(model);
    } catch (error) {
      const maybeStatus =
        (error as { status?: number })?.status ??
        (error as { statusCode?: number })?.statusCode ??
        (error as { response?: { status?: number } })?.response?.status;
      if (maybeStatus === 404) {
        return null;
      }
      throw error;
    }
  },

  /** Current run sequence and in-flight flag for a connector feature. */
  getConnectorInvocationSequence: async (
    tenantId: UUID,
    connectorId: UUID,
    featureId: UUID
  ): Promise<ConnectorFeatureCurrentSequence> => {
    const res = await api.getConnectorInvocationSequence(
      tenantId,
      connectorId,
      featureId
    );

    if (!res.ok) {
      throw new Error(
        res.error?.message ?? 'Failed to fetch connector feature sequence'
      );
    }

    const model = unwrapData<ApiConnectorFeatureInvocationSequence>(res);
    if (!model) {
      throw new Error(
        'Failed to fetch connector feature sequence: missing result'
      );
    }

    return extractInvocationSequence(model);
  },

  /** Request a connector feature invocation (RequestConnectorInvocation). */
  requestConnectorInvocation: async (
    tenantId: UUID,
    connectorId: UUID,
    featureId: UUID,
    runSequence: number,
    payload?: unknown
  ): Promise<InvokeConnectorFeatureResult> => {
    const body: RequestConnectorInvocationBody =
      payload !== undefined ? { payload } : {};
    const res = await api.requestConnectorInvocation(
      tenantId,
      connectorId,
      featureId,
      runSequence,
      body as Parameters<typeof api.requestConnectorInvocation>[4]
    );

    if (!res.ok) {
      if (res.status === 409) {
        throw new Error(
          `INVOCATION_CONFLICT: ${res.error?.message ?? 'This feature+sequence was already invoked'}`
        );
      }

      if (res.status === 412) {
        throw new Error(
          `INVOCATION_PRECONDITION: ${res.error?.message ?? 'Invalid sequence or invocation in flight'}`
        );
      }

      throw new Error(
        res.error?.message ?? 'Failed to invoke connector feature'
      );
    }

    const model = unwrapData<RequestConnectorInvocationResult>(res);
    if (!model) {
      throw new Error('Failed to invoke connector feature: missing result');
    }

    return extractInvocationResult(model);
  },

  /** Disable a connector (toggle off via changeConnectorStatus) */
  disableConnector: async (
    tenantId: UUID,
    connectorId: UUID
  ): Promise<boolean> => {
    const res = await api.changeConnectorStatus(tenantId, connectorId, {
      status: 'disabled',
    });
    if (!extractAccepted(res)) {
      throw new Error(res.error?.message ?? 'Failed to disable connector');
    }
    return true;
  },

  /** Enable a connector */
  enableConnector: async (
    tenantId: UUID,
    connectorId: UUID
  ): Promise<boolean> => {
    const res = await api.changeConnectorStatus(tenantId, connectorId, {
      status: 'enabled',
    });
    if (!extractAccepted(res)) {
      throw new Error(res.error?.message ?? 'Failed to enable connector');
    }
    return true;
  },

  /** List invocation statuses for a connector feature */
  listConnectorInvocationStatuses: async (
    tenantId: UUID,
    connectorId: UUID,
    featureId: UUID,
    query?: { limit?: number; next?: string }
  ): Promise<ConnectorInvocationStatuses> => {
    const res = await api.listConnectorInvocationStatuses(
      tenantId,
      connectorId,
      featureId,
      query
    );
    if (!res.ok) {
      throw new Error(
        res.error?.message ??
          'Failed to list connector feature invocation statuses'
      );
    }
    const model = unwrapData<ApiPageConnectorInvocationStatusResponse>(res);
    if (!model) {
      return { items: [] };
    }
    return extractInvocationStatuses(model);
  },

  /** Find invocation status by run sequence */
  findConnectorInvocationStatus: async (
    tenantId: UUID,
    connectorId: UUID,
    featureId: UUID,
    runSequence: number
  ): Promise<ConnectorInvocationStatus | null> => {
    try {
      const res = await api.findConnectorInvocationStatus(
        tenantId,
        connectorId,
        featureId,
        runSequence
      );
      if (!res.ok) {
        if (res.status === 404) {
          return null;
        }
        throw new Error(
          res.error?.message ??
            'Failed to find connector feature invocation status'
        );
      }
      const model = unwrapData<ApiConnectorInvocationStatusResponse>(res);
      if (!model) return null;
      return { ...extractInvocationStatus(model), runSequence };
    } catch (error) {
      const maybeStatus =
        (error as { status?: number })?.status ??
        (error as { statusCode?: number })?.statusCode ??
        (error as { response?: { status?: number } })?.response?.status;
      if (maybeStatus === 404) {
        return null;
      }
      throw error;
    }
  },

  /** Stop a connector feature invocation (StopConnectorInvocation) */
  stopConnectorInvocation: async (
    tenantId: UUID,
    connectorId: UUID,
    featureId: UUID,
    runSequence: number,
    message?: string
  ): Promise<boolean> => {
    const stopBody: StopConnectorInvocationBody = {
      message: message ?? 'Stopped by user',
    };
    const res = await api.stopConnectorInvocation(
      tenantId,
      connectorId,
      featureId,
      runSequence,
      stopBody as Parameters<typeof api.stopConnectorInvocation>[4]
    );
    if (res.status === 409) {
      throw new Error(
        `STOP_CONFLICT: ${res.error?.message ?? 'This run cannot be stopped'}`
      );
    }
    if (!extractAccepted(res)) {
      throw new Error(
        res.error?.message ?? 'Failed to stop connector feature invocation'
      );
    }
    return true;
  },

  /** Delete (remove) a connector */
  deleteConnector: async (
    tenantId: UUID,
    connectorId: UUID
  ): Promise<boolean> => {
    const res = await api.removeConnector(tenantId, connectorId);
    return extractBoolean(res);
  },

  /** Current invocation statuses for all features of a connector in a single call */
  listCurrentConnectorInvocationStatusesByConnector: async (
    tenantId: UUID,
    connectorId: UUID
  ): Promise<Record<UUID, ConnectorInvocationStatus>> => {
    const res = await api.listCurrentConnectorInvocationStatusesByConnector(
      tenantId,
      connectorId
    );
    if (!res.ok) {
      throw new Error(
        res.error?.message ??
          'Failed to list current connector invocation statuses'
      );
    }
    const models = unwrapData<Array<ApiConnectorInvocationStatusResponse>>(res);
    if (!models) return {};
    const result: Record<string, ConnectorInvocationStatus> = {};
    for (const model of models) {
      if (model.feature_id) {
        result[model.feature_id] = extractInvocationStatus(model);
      }
    }
    return result;
  },

  /** Latest projected invocation status for a connector feature */
  getCurrentConnectorInvocationStatus: async (
    tenantId: UUID,
    connectorId: UUID,
    featureId: UUID
  ): Promise<ConnectorInvocationStatus | null> => {
    try {
      const res = await api.getCurrentConnectorInvocationStatus(
        tenantId,
        connectorId,
        featureId
      );
      if (!res.ok) {
        if (res.status === 404) {
          return null;
        }
        throw new Error(
          res.error?.message ??
            'Failed to get current connector feature invocation status'
        );
      }
      const model = unwrapData<ApiConnectorInvocationStatusResponse>(res);
      if (!model || !model.status) return null;
      return extractInvocationStatus(model);
    } catch (error) {
      const maybeStatus =
        (error as { status?: number })?.status ??
        (error as { statusCode?: number })?.statusCode ??
        (error as { response?: { status?: number } })?.response?.status;
      if (maybeStatus === 404) {
        return null;
      }
      throw error;
    }
  },
};
